import { Button, Modal, Tag, Icon, Tooltip } from "antd";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import * as documentActions from "~/containers/Documents/actions";
import SettingsVirtualTable from "../components/SettingsVirtualTable";
import { isPaymentRequired } from '~/helpers/isPaymentRequired';

var DEPARTMENTS = {
  'legalizacao': { code: 1, label: 'Legalização', color: '#722ed1', bg: '#f9f0ff' },
  'administrativo': { code: 2, label: 'Administrativo', color: '#1890ff', bg: '#e6f7ff' },
  'contabil': { code: 3, label: 'Contábil', color: '#13c2c2', bg: '#e6fffb' },
  'fiscal': { code: 4, label: 'Fiscal', color: '#52c41a', bg: '#f6ffed' },
  'pessoal': { code: 5, label: 'Pessoal', color: '#fa8c16', bg: '#fff7e6' },
  'sucesso-do-cliente': { code: 6, label: 'Sucesso do Cliente (CS)', color: '#eb2f96', bg: '#fff0f6' },
};

function getDeptMetaByCode(code) {
  var keys = Object.keys(DEPARTMENTS);
  for (var i = 0; i < keys.length; i++) {
    if (DEPARTMENTS[keys[i]].code === code) return DEPARTMENTS[keys[i]];
  }
  return { label: '—', color: '#8c8c8c', bg: '#fafafa' };
}

export var SporadicDocumentTypes = function(props) {
  var departmentKey = props.department || 'legalizacao';
  var dept = DEPARTMENTS[departmentKey] || DEPARTMENTS['legalizacao'];
  var departmentCode = dept.code;
  var searchTerm = props.searchTerm || '';
  var addTrigger = props.addTrigger || 0;

  var typesState = useState([]);
  var types = typesState[0];
  var setTypes = typesState[1];

  var documentTypes = useSelector(function(state) { return state.documents.types; });
  var dispatch = useDispatch();

  useEffect(function() {
    setTypes(documentTypes);
  }, [documentTypes]);

  function handleDelete(id) {
    Modal.confirm({
      title: "Você realmente deseja remover este tipo de documento?",
      content: "Esta ação não poderá ser desfeita.",
      okText: "Sim",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async function() {
        setTypes(types.filter(function(item) { return item.id !== id; }));
        await dispatch(documentActions.deleteDocumentType(id));
      },
    });
  }

  async function handleCreate(newType) {
    await dispatch(documentActions.createDocumentType({
      description: newType.description,
      department: newType.department,
      requires_payment: newType.requires_payment,
    }));
  }

  async function handleUpdate(updatedType) {
    setTypes(types.map(function(type) {
      return type.id !== updatedType.id ? type : updatedType;
    }));
    await dispatch(documentActions.updateDocumentType(updatedType.id, {
      description: updatedType.description,
      department: updatedType.department,
      requires_payment: updatedType.requires_payment,
    }));
  }

  var hasSearch = !!(searchTerm && searchTerm.trim());

  // When searching, expand the scope to ALL departments so results aren't hidden behind
  // the current sidebar selection. Without search, stay scoped to the selected department.
  var filtered = (hasSearch
    ? types
    : types.filter(function(type) { return type.department === departmentCode; })
  ).sort(function(a, b) {
    if (hasSearch && a.department !== b.department) return (a.department || 0) - (b.department || 0);
    return (a.description || '').localeCompare(b.description || '');
  });

  var handleTogglePayment = function(record) {
    var updated = Object.assign({}, record, { requires_payment: isPaymentRequired(record.requires_payment) ? false : true });
    handleUpdate(updated);
  };

  var columns = [
    { key: 'description', title: 'Nome', render: function(record) { return record.description; } },
  ];

  if (hasSearch) {
    columns.push({
      key: 'department', title: 'Departamento', width: 220,
      render: function(record) {
        var meta = getDeptMetaByCode(record.department);
        return React.createElement('span', {
          style: {
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500,
            background: meta.bg, color: meta.color,
            border: '1px solid ' + meta.color + '33',
          }
        },
          React.createElement('span', {
            style: { width: 7, height: 7, borderRadius: '50%', background: meta.color }
          }),
          meta.label
        );
      }
    });
  }

  columns.push(
    {
      key: 'requires_payment', title: 'Tipo', width: 180,
      render: function(record) {
        var isRequired = isPaymentRequired(record.requires_payment);
        return React.createElement(Tooltip, {
          title: 'Clique para alterar para ' + (isRequired ? 'Informativo' : 'Pagável')
        },
          React.createElement('div', {
            onClick: function(e) { e.stopPropagation(); handleTogglePayment(record); },
            style: {
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 12px', borderRadius: 14, cursor: 'pointer',
              fontSize: 12, fontWeight: 500, transition: 'all 0.15s',
              background: isRequired ? '#e6f7ff' : '#f6ffed',
              color: isRequired ? '#1890ff' : '#52c41a',
              border: '1px solid ' + (isRequired ? '#91d5ff' : '#b7eb8f'),
            }
          },
            React.createElement(Icon, {
              type: isRequired ? 'dollar' : 'file-text',
              style: { fontSize: 12 }
            }),
            isRequired ? 'Pagável' : 'Informativo',
            React.createElement(Icon, {
              type: 'swap',
              style: { fontSize: 10, opacity: 0.5 }
            })
          )
        );
      },
      filterValue: function(record) { return isPaymentRequired(record.requires_payment) ? 'true' : 'false'; },
      filters: [
        { value: 'true', label: 'Pagável' },
        { value: 'false', label: 'Informativo' },
      ]
    }
  );

  return (
    <div>
      <SettingsVirtualTable
        data={filtered}
        columns={columns}
        searchTerm={searchTerm}
        onInlineEdit={function(record, newValue) {
          if (!newValue || !newValue.trim() || newValue.trim() === record.description) return;
          handleUpdate(Object.assign({}, record, { description: newValue.trim() }));
        }}
        onInlineDelete={function(record) { handleDelete(record.id); }}
        onQuickAdd={function(value) {
          handleCreate({ description: value, department: departmentCode, requires_payment: false });
        }}
        addTrigger={addTrigger}
        addLabel={'Novo Documento - ' + dept.label}
      />
    </div>
  );
};
